import { type FC, useCallback, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  type ContactFormState,
  useContactFormApiPostMutation,
} from '../../../../contact-form'
import { FriendlyCaptchaChallenge } from '../../../../friendly-captcha'
import {
  type OpenableControls,
  useOpenableControls,
} from '../../../../generic-hooks'
import { LoginRegistrationUserLoginModal } from '../../../../login-registration'
import { refetchUseApiOrdersSwrGetOrders } from '../../../../order'
import { useDirectContactFormOrderLoginModal } from '../../../hooks/useDirectContactFormOrderLoginModal'
import { useDirectContactFormUpdateFormStateWithUserData } from '../../../hooks/useDirectContactFormUpdateFormStateWithUserData'
import { useDirectFormDialogContext } from '../DirectContactFormDialogContext'
import { DirectContactFormEmailInputField } from '../DirectContactFormEmailInputField'
import { DirectContactFormMessageInputField } from '../DirectContactFormMessageInputField'
import { DirectContactFormOrderInputField } from '../DirectContactFormOrderInputField'
import { DirectContactFormOrderSelectionDialog } from '../DirectContactFormOrderSelection/DirectContactFormOrderSelectionDialog'
import { DirectContactFormPhoneNumberField } from '../DirectContactFormPhoneNumberField'
import { DirectContactFormPrefilledOrderInputField } from '../DirectContactFormPrefilledOrderInputField'
import { DirectContactFormSelectSubjectField } from '../DirectContactFormSelectSubjectField'
import { DirectContactFormSubmitButton } from '../DirectContactFormSubmitButton'
import { DirectContactFormTextInfoSection } from '../DirectContactFormTextInfoSection'

export const DirectContactFormOrderTypeForm: FC<{
  formState: ContactFormState
  onFormSubmittedWithSuccess: () => void
  userModalControls: OpenableControls
}> = ({ formState, onFormSubmittedWithSuccess, userModalControls }) => {
  const loginModalControls = useOpenableControls()

  const { prefilledSubOrder, setSelectedSubOrder } =
    useDirectFormDialogContext()

  const isLoggedIn = useDirectContactFormOrderLoginModal(loginModalControls)

  const productSelectionModalControls = useOpenableControls()

  const submitMutation = useContactFormApiPostMutation()

  const [isFriendlyCaptchaDone, setIsFriendlyCaptchaDone] =
    useState<boolean>(false)

  useDirectContactFormUpdateFormStateWithUserData(formState)

  const captchaDoneCallback = useCallback(
    (fcSolution: string) => {
      formState.setValue(fcSolution, formState.model, 'fcSolution')
      setIsFriendlyCaptchaDone(true)
    },
    [formState],
  )

  useEffect(() => {
    if (prefilledSubOrder) {
      setSelectedSubOrder(prefilledSubOrder)
    }
  }, [prefilledSubOrder, setSelectedSubOrder])

  return (
    <>
      <p className="mb-2 text-xs text-dark-primary-strong">
        <FormattedMessage id="general.form.label.mandatoryFieldIndicator" />
      </p>

      {isLoggedIn ? null : (
        <DirectContactFormEmailInputField formState={formState} />
      )}

      <DirectContactFormPhoneNumberField formState={formState} />

      <DirectContactFormSelectSubjectField formState={formState} />

      {prefilledSubOrder ? (
        <DirectContactFormPrefilledOrderInputField
          formState={formState}
          productSelectionModalControls={productSelectionModalControls}
        />
      ) : (
        <DirectContactFormOrderInputField
          formState={formState}
          isLoggedIn={isLoggedIn}
          productSelectionModalControls={productSelectionModalControls}
        />
      )}

      <DirectContactFormMessageInputField formState={formState} />

      <DirectContactFormTextInfoSection />

      <FriendlyCaptchaChallenge
        className="mb-3.5"
        doneCallback={captchaDoneCallback}
        startMode="auto"
        whenThisPropChangesResetCaptcha={submitMutation.error}
      />

      <DirectContactFormSubmitButton
        formState={formState}
        isFriendlyCaptchaDone={isFriendlyCaptchaDone}
        modalControls={userModalControls}
        onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
        submitMutation={submitMutation}
      />

      <LoginRegistrationUserLoginModal
        afterLoggedIn={() => {
          void refetchUseApiOrdersSwrGetOrders()
          loginModalControls.handleClose()
        }}
        onClose={loginModalControls.handleClose}
        open={loginModalControls.isOpen}
      />

      <DirectContactFormOrderSelectionDialog
        formState={formState}
        modalControls={productSelectionModalControls}
      />
    </>
  )
}
